import {
	QueryObserverOptions,
	useMutation,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query'
import { useAuth } from '../auth/useAuth'
import { ContactService } from '../../lib/api/contact.services'
import {
	GetContactsDataResponse,
	ContactDataProps,
	ContactData,
} from '../../lib/api/types/contact.interfaces'
import toastCaller from '../../lib/utils/toastCaller'
import { handleApiError } from '../../lib/utils/handleApiError'

export const useContactItems = ({ offset, limit }: { offset?: number; limit?: number },
	options?: Partial<QueryObserverOptions<GetContactsDataResponse>>
) => {
	const { isAuthenticated } = useAuth()
	return useQuery<GetContactsDataResponse>({
		queryKey: ['contacts', offset, limit],
		queryFn: () => ContactService.getContacts({ offset, limit }),
		enabled: isAuthenticated,
		staleTime: 1000 * 5,
		retry: 1,
		...options,
	})
}

export const useAddContact = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['addContact'],
		mutationFn: ({
			data,
			invalidate,
		}: {
			data: ContactDataProps
			invalidate?: boolean
		}) => ContactService.addContact({ data}),
		onError: (error) => handleApiError(error),
		onSuccess: (_) => {
			queryClient.invalidateQueries({
				queryKey: ['contacts'],
			})
			toastCaller({ message: 'Added', type: 'success' })
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}
export const useDeleteContact = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['deleteContact'],
		mutationFn: ({
			id,
		}: {
			id: string
			invalidate?: boolean
		}) => ContactService.deleteContact({ id}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate = true }) => {
			toastCaller({ message: 'Deleted', type: 'success' })
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['contacts'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const useUpdateContact = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['updateContact'],
		mutationFn: ({
			data,
		}: {
			data: ContactData
			invalidate?: boolean
		}) => ContactService.updateContact({ data}),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate = true }) => {
			toastCaller({ message: 'Updated', type: 'success' })
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['contacts'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}

export const usePinContact = () => {
	const queryClient = useQueryClient()
	const mutation = useMutation({
		mutationKey: ['pinContact'],
		mutationFn: ({
			id,
			pin,
			invalidate,
		}: {
			id: string
			pin: boolean
			invalidate?: boolean
		}) => ContactService.pinContact({ id, pin }),
		onError: (error) => handleApiError(error),
		onSuccess: (_, { invalidate = true }) => {
			toastCaller({ message: 'Pinned', type: 'success' })
			if (invalidate) {
				queryClient.invalidateQueries({
					queryKey: ['contacts'],
				})
			}
		},
	})

	type MutationAsyncParameters = Parameters<typeof mutation.mutateAsync>
	const mutateAsync = async (...params: MutationAsyncParameters) => {
		try {
			await mutation.mutateAsync(...params)
		} catch (error) {
			return
		}
	}

	return { ...mutation, mutateAsync }
}
